/**
 * Meetings
 * Talkroute
 *
 * Created by Vel Genov on 07/22/21
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

/**
 * The external dependencies
 */
import React, { useEffect, useState } from "react";
import { Grid } from "semantic-ui-react";
import InfiniteScroll from "react-infinite-scroll-component";

/**
 * Styles
 */
import {
  MeetingCardsScroll,
  MeetingCard,
  MeetingCardHead,
  MeetingCardTitle,
  MeetingCardItem,
  MeetingCardItemTitle,
  MeetingCardItemActions,
  MeetingCardItemText,
  MeetingCardButtonPrimary,
  MeetingCardButtonSecondary,
  MeetingNoItemsText,
} from "./Details.styles";
import { useMoxtra } from "@helpers/hooks/useMoxtra.hook";
import { useDispatch } from "react-redux";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import { IScheduledMeeting } from "@models/Moxtra.models";
import { push } from "connected-react-router";
import {
  MeetingsRouteActions,
  routeUrlBuilders,
} from "@constants/routes";
import { 
  LOADING, 
  MEETING_ID, 
  MEETING_LINK_COPIED, 
  NO_MEETINGS_TODAY, 
  NO_UPCOMING_MEETINGS, 
  TODAY 
} from "./constants";
import { DeleteMeetingDialog } from "./DeleteMeetingDialog";
import uiString from '@constants/uiString';
import { useToasts } from "react-toast-notifications";
import { endTime, filterRecurrentMeetings, startTime } from "@helpers/meetings";
import { useDeleteMeetingMutation, useGetAllMeetingsQuery } from "@services/Moxtra.slice";
import { isAxiosError } from "@utils";

/**
 * Meetings Component
 */
export default function Meetings() {
  const {data: meetingsList = [], isFetching} = useGetAllMeetingsQuery(undefined, {
    refetchOnReconnect: true,
    pollingInterval: 1000 * 90,
  });
  const [deleteMeeting, {isLoading: deletionMeetInProgress, isError, error, }] = useDeleteMeetingMutation();
  const dispatch = useDispatch();
  const [meetingToDelete, setMeetingToDelete] = useState<IScheduledMeeting | null>(null);
  const { addToast } = useToasts();
  const _userId = useAppSelector((state) => state.authentication.userId);
  const userId = String(_userId);

  useEffect(() => {
    if (isAxiosError(error) && error.response?.status !== 400) {
      addToast(error.message, { appearance: 'error', autoDismiss: true });
    }
  }, [error])

  const openMeeting = (meet: IScheduledMeeting) => {
    dispatch(
      push(
        routeUrlBuilders.MEETINGS_IFRAME(
          MeetingsRouteActions.JOIN_MEETING,
          meet.session_key
        )
      )
    );
  };

  const handleDelete = async (meetToDel: IScheduledMeeting) => {
    try {
      await deleteMeeting({meeting: meetToDel});
    } catch (error) {
      throw error;
    }
  };

  /**
   * Copy the current meeting link to the clipboard and alerts the user 
   * 
   * @param sessionKey string with the current meeting ID
   */
  const copyMeetingHandler = (sessionKey: string) => {
    const url = routeUrlBuilders.JOIN_MEETING_LINK(sessionKey);
    navigator.clipboard.writeText(url);
    addToast(MEETING_LINK_COPIED, { appearance: 'success', autoDismiss: true });
  };

  const todaysMeetings: IScheduledMeeting[] = [];
  const futureMeetings: IScheduledMeeting[] = [];

  for (let meeting of meetingsList) {
    const today = new Date();
    const meetingDate = new Date(
      meeting.scheduled_starts
    );
    const meetingIsToday =
      meetingDate.getFullYear() === today.getFullYear() &&
      meetingDate.getMonth() === today.getMonth() &&
      meetingDate.getDate() === today.getDate();
    if (meetingIsToday) {
      todaysMeetings.push(meeting);
    } else {
      futureMeetings.push(meeting);
    }
  };

  return (
    <>
    <Grid.Column
      textAlign="left"
      style={{
        height: "calc(100% - 60px)",
        backgroundColor: "var(--color-white)",
      }}
    >
      <MeetingCardsScroll>
        <InfiniteScroll
          loader={<h4>{LOADING}</h4>}
          dataLength={0}
          next={() => {}}
          hasMore={false}
          style={{
            overflowY: "visible",
            overflowX: "visible",
          }}
        >
          <MeetingCard>
            <MeetingCardHead
              style={{
                borderBottom:
                  todaysMeetings.length === 0
                    ? "1px solid var(--color-dark-grey)"
                    : "none",
              }}
            >
              <MeetingCardTitle>{TODAY}</MeetingCardTitle>
            </MeetingCardHead>

            {futureMeetings.length > 0 && todaysMeetings.length === 0 && (
              <MeetingNoItemsText>{NO_MEETINGS_TODAY}</MeetingNoItemsText>
            )}

            {todaysMeetings.map((meeting) => (
              <MeetingCardItem key={meeting.session_key + meeting.scheduled_starts}>
                <MeetingCardItemTitle>{meeting.topic}</MeetingCardItemTitle>

                <MeetingCardItemActions>
                  <MeetingCardButtonPrimary
                    onClick={() => openMeeting(meeting)}
                  >
                    {uiString.START}
                  </MeetingCardButtonPrimary>
                  {meeting.host.id === userId && <MeetingCardButtonSecondary
                    onClick={() => setMeetingToDelete(meeting)}
                    >
                    {uiString.DELETE}
                  </MeetingCardButtonSecondary>}
                  <MeetingCardButtonSecondary
                    onClick={() => copyMeetingHandler(meeting.session_key)}
                  >
                    {uiString.LINK}
                  </MeetingCardButtonSecondary>
                </MeetingCardItemActions>

                <MeetingCardItemText>
                  {startTime(meeting.scheduled_starts, true)} -{" "}
                  {endTime(meeting.scheduled_ends)}
                </MeetingCardItemText>
                <MeetingCardItemText>
                  {MEETING_ID} {meeting.session_key}
                </MeetingCardItemText>
              </MeetingCardItem>
            ))}
          </MeetingCard>
          <MeetingCard
            style={{
              borderTop: "none"
            }}
          >
            {futureMeetings.length > 0 && (
              <MeetingCardHead>
                <MeetingCardTitle>{uiString.FUTURE}</MeetingCardTitle>
              </MeetingCardHead>
            )}
            {filterRecurrentMeetings(futureMeetings).map((meeting) => (
              <MeetingCardItem key={meeting.session_key + meeting.scheduled_starts}>
                <MeetingCardItemTitle>{meeting.topic}</MeetingCardItemTitle>

                <MeetingCardItemActions>
                  <MeetingCardButtonPrimary
                    onClick={() => openMeeting(meeting)}
                  >
                    {uiString.START}
                  </MeetingCardButtonPrimary>
                  {/* <MeetingCardButtonSecondary>Edit</MeetingCardButtonSecondary> */}
                  {meeting.host.id === userId && <MeetingCardButtonSecondary
                    onClick={() => setMeetingToDelete(meeting)}
                  >
                    {uiString.DELETE}
                  </MeetingCardButtonSecondary>}
                  <MeetingCardButtonSecondary
                    onClick={() => copyMeetingHandler(meeting.session_key)}
                  >
                    {uiString.LINK}
                  </MeetingCardButtonSecondary>
                </MeetingCardItemActions>

                <MeetingCardItemText>
                  {startTime(meeting.scheduled_starts)} -{" "}
                  {endTime(meeting.scheduled_ends)}
                </MeetingCardItemText>
                <MeetingCardItemText>
                  {MEETING_ID} {meeting.session_key}
                </MeetingCardItemText>
              </MeetingCardItem>
            ))}
          </MeetingCard>

          {meetingsList.length === 0 && !isFetching && (
            <MeetingNoItemsText>{NO_UPCOMING_MEETINGS}</MeetingNoItemsText>
          )}
        </InfiniteScroll>
      </MeetingCardsScroll>
      <DeleteMeetingDialog
        open={Boolean(meetingToDelete)}
        onClose={() => setMeetingToDelete(null)}
        sessionKey={meetingToDelete?.session_key as string}
        handleDelete={() => handleDelete(meetingToDelete as IScheduledMeeting)}
      />
    </Grid.Column>
    </>
  );
}
